<template>
  <div style="margin:20px">
    <!-- Header -->
    <FiPageRail title="Future Map" />

    <!-- Filters & Controls: wrapping groups, so a phone gets stacked
         full-width-safe controls instead of one overflowing centered line -->
    <div>
      <!-- Row 1: Date range -->
      <div class="map-controls">
        <span class="mc-group">
          <a href="#" class="white-link" @click.prevent="shift_detail_graph_date(-1)"><</a>
          <date-picker v-model="details_fromDate" :config="options" class="mc-datepicker"/>
          <span>to</span>
          <date-picker v-model="details_toDate" :config="options" class="mc-datepicker"/>
          <a href="#" class="white-link" @click.prevent="shift_detail_graph_date(1)">></a>
        </span>
      </div>

      <!-- Row 2: Filters & Search -->
      <div class="map-controls" style="margin-top: 10px">
        <span class="mc-group">
          <span>Driver:</span>
          <b-form-select
            v-if="driver_options.length > 0"
            v-model="selected_driver"
            :options="driver_options"
            class="mc-select mc-select--driver"
          />
          <b-spinner v-else style="width: 1.5rem; height: 1.5rem;" label="Loading..."></b-spinner>
        </span>

        <span class="mc-group">
          <span>Vehicle:</span>
          <b-form-select
            v-if="vehicle_options.length > 0"
            v-model="selected_vehicle"
            :options="vehicle_options"
            class="mc-select mc-select--vehicle"
          />
          <b-spinner v-else style="width: 1.5rem; height: 1.5rem;" label="Loading..."></b-spinner>
        </span>

        <span v-if="user.types && user.types[user.tenant] === 'TBN'" class="mc-group">
          <span>Booking Category:</span>
          <b-form-select
            v-model="selected_booking_category"
            :options="booking_category_options"
            class="mc-select mc-select--category"
          />
        </span>

        <b-form-input
          v-if="user.types && user.types[user.tenant] === 'TBN'"
          v-model="booking_search"
          class="mc-search"
          placeholder="search by charterID"
        />

        <b-button @click.prevent="load_data()" :disabled="loading">Search</b-button>
      </div>
    </div>

    <div v-if="items_full.length" class="map-controls mc-typerow">
      <b>Vehicle Types:</b>
      <b-form-checkbox-group
        v-model="selected_vehicle_type"
        :options="vehicle_type_options"></b-form-checkbox-group>
    </div>
    <div v-else>
      <br/>
      <div v-if="loading" class="text-center text-primary my-2">
        &nbsp;&nbsp;<b-spinner class="align-middle"/>
      </div>
    </div>

    <!-- Time Slider -->
    <div v-if="items_full.length" class="d-flex align-items-center mc-sliderrow">
      <b>Time Range:</b>&nbsp;
      <span><b>{{ formatSliderStartTime }}</b></span>
      &nbsp;&nbsp;
      <input
        type="range"
        :min="sliderMin"
        :max="sliderMax"
        :step="sliderStep"
        v-model.number="sliderTime"
        @input="simulatePositions"
        style="flex:1"
      />
      &nbsp;
      <span><b>{{ formatSliderEndTime }}</b></span>
      &nbsp;&nbsp;
        <img v-if="!autoSimulatePositions" v-on:click.prevent="autoSimulatePositions = !autoSimulatePositions" src="/play.png" width="30"/>
        <img v-if="autoSimulatePositions" v-on:click.prevent="autoSimulatePositions = !autoSimulatePositions" src="/pause.png" width="30"/>
      &nbsp;&nbsp;
        <span style="color: darkgreen"><b>{{ formatSliderTime }}</b></span>
    </div>

    <!-- Map -->
    <div class="my-2">
      <b-collapse id="trip-map-collapse" v-model="showMap" class="mt-2">
        <GmapMap
          ref="mapRef"
          :center="mapCenter"
          :zoom="mapZoom"
          :options="themed_map_options"
          @click="closeInfoWindow"
          style="width:100%;height:600px;"
        >
          <GmapPolyline
            v-for="poly in polylines"
            :key="poly.id"
            :path="poly.path"
            :options="{ strokeColor: poly.color, strokeWeight: 2 }"
          />
          <GmapInfoWindow
            v-if="infoWindow.open"
            :options="{ position: infoWindow.position }"
            @closeclick="infoWindow.open = false"
          >
            <div v-html="infoWindow.content"/>
          </GmapInfoWindow>
        </GmapMap>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { get_tbn_vehicle_map_future_charter_data, get_tbn_vehicle_map_future_data, get_tbn_booking_categories } from '@/networking/tbn_apis'
import { get_cm_vehicle_map_future_data, get_driver_list, get_vehicle_list } from '../networking/apis'
import { mapState } from 'vuex'
import { justDate } from '../helpers/dates'
import { isLatLngInUsCa } from '../helpers/geo_bounds'
import moment from 'moment'
import * as VueGoogleMaps from 'vue2-google-maps'
import { gmapApi } from 'vue2-google-maps'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'

export default {
  name: 'TripLinkerReportTbn',
  components: {
    GmapMap: VueGoogleMaps.Map,
    GmapPolyline: VueGoogleMaps.Polyline,
    GmapInfoWindow: VueGoogleMaps.InfoWindow
  },
  data() {
    return {
      items: [],
      items_full: [],
      summary: [],
      booking_search: '',
      vehicle_type_options: [],
      selected_vehicle_type: [],
      loading: false,
      details_fromDate: justDate(moment().add(1, 'days')),
      details_toDate: justDate(moment().add(3, 'days')),
      showMap: true,
      mapCenter: {lat: 39.8283, lng: -98.5795},
      mapZoom: 5,
      segments: [],
      polylines: [],
      staticMarkers: [],
      vehicleGroups: {},
      vehicleIds: [],
      vehicleMarkers: [],
      sliderTime: null,
      autoSimulatePositions: false,
      sliderStep: 15 * 60 * 1000,
      infoWindow: {open: false, position: null, content: ''},
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true
      },
      mapStyle: {
        mapTypeControl: false,
        styles: [
          {
            "featureType": "landscape",
            "stylers": [
              {"color": "#F3F4F9"}
            ]
          }, {
            "featureType": "water",
            "stylers": [
              {"color": "#C1CDE4"}
            ]
          },
          {
            "featureType": "landscape.natural.terrain",
            "stylers": [
              {"color": "#E0EDCA"}
            ]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{color: '#DDDDDD'}]
          },
          {
            featureType: 'road',
            elementType: 'geometry.fill',
            stylers: [{color: '#FFFFFF'}]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.fill',
            stylers: [{color: '#FEF8D6'}]
          },
          {
            featureType: 'transit',
            stylers: [{"visibility": "off"}]
          },
          {
            featureType: 'poi',
            stylers: [{"visibility": "off"}]
          },
        ]
      },
      driver_options: [],
      vehicle_options: [],
      selected_driver: '',
      selected_vehicle: '',
      booking_category_options: [{ value: '', text: 'All Booking Categories' }],
      selected_booking_category: '',
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    google: gmapApi,
    // the light mapStyle in data stays untouched; midnight users get the
    // dark basemap (in-shell map pages only -- standalone maps stay light)
    themed_map_options() {
      const midnight = (this.$store.state.profile || {}).site_theme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    sliderMin() {
      return moment(this.details_fromDate, 'MM-DD-YYYY').startOf('day').valueOf()
    },
    sliderMax() {
      return moment(this.details_toDate, 'MM-DD-YYYY').endOf('day').valueOf()
    },
    formatSliderTime() {
      const t = Number(this.sliderTime)
      return isNaN(t) ? '' : moment(t).format('MM-DD-YYYY HH:mm')
    },
    formatSliderStartTime() {
      const t = Number(this.sliderMin)
      return isNaN(t) ? '' : moment(t).format('MM-DD-YYYY')
    },
    formatSliderEndTime() {
      const t = Number(this.sliderMax)
      return isNaN(t) ? '' : moment(t).format('MM-DD-YYYY')
    }
  },
  created() {
    this.load_drivers()
    this.load_vehicles()
    if (this.user && this.user.types && this.user.types[this.user.tenant] === 'TBN') {
      this.load_booking_categories()
    }
  },
  watch: {
    'user.tenant'(newTenant, oldTenant) {
      if (!oldTenant || newTenant === oldTenant) return

      this.selected_driver = ''
      this.selected_vehicle = ''
      this.booking_search = ''
      this.selected_booking_category = ''

      this.driver_options = []
      this.vehicle_options = []
      this.booking_category_options = [{ value: '', text: 'All Booking Categories' }]
      this.resetForTenantChange()

      this.load_drivers()
      this.load_vehicles()
      this.load_booking_categories()
    },

    booking_search() {
      this.filter_items()
    },
    selected_vehicle_type() {
      this.filter_items()
    },
    autoSimulatePositions() {
      if (this.autoSimulatePositions)
        this.runSimulatePositions()
    },
    booking_search() {
      this.filter_items()
    },
    selected_vehicle_type() {
      this.filter_items()
    },
    autoSimulatePositions() {
      if (this.autoSimulatePositions)
        this.runSimulatePositions()
    }
  },
  methods: {
    async load_booking_categories() {
      try {
        const { value: { data = [] } } = await get_tbn_booking_categories(this.user.tenant)
        const opts = Array.isArray(data) ? data.slice() : []
        opts.unshift({ value: '', text: 'All Booking Categories' })
        this.booking_category_options = opts
      } catch (e) {
        console.error('booking categories error', e)
        this.booking_category_options = [{ value: '', text: 'All Booking Categories' }]
      }
    },

    async load_drivers() {
      try {
        const { value: { data = [] } } = await get_driver_list(this.user.tenant)
        const opts = Array.isArray(data) ? data.slice() : []
        if (!opts.find(o => o && (o.value === '' || o.value === null))) {
          opts.unshift({ value: '', text: 'All Drivers' })
        }
        this.driver_options = opts
      } catch (e) {
        console.error('driver list error', e)
        this.driver_options = [{ value: '', text: 'All Drivers' }]
      }
    },
    async load_vehicles() {
      try {
        const start = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const { value: { data = [] } } = await get_vehicle_list(this.user.tenant, start)
        const opts = Array.isArray(data) ? data.slice() : []
        if (!opts.find(o => o && (o.value === '' || o.value === null))) {
          opts.unshift({ value: '', text: 'All Vehicles' })
        }
        this.vehicle_options = opts
      } catch (e) {
        console.error('vehicle list error', e)
        this.vehicle_options = [{ value: '', text: 'All Vehicles' }]
      }
    },

    closeInfoWindow() {
      this.infoWindow.open = false
    },

    async load_data() {
      this.loading = true
      this.clearMarkers()

      this.items = []
      this.items_full = []
      this.vehicle_type_options = []

      const start = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const end   = moment(this.details_toDate,   'MM-DD-YYYY').format('YYYY-MM-DD')

      try {
        let resp
        if (this.user && this.user.types && this.user.types[this.user.tenant] === 'CM') {
          resp = await get_cm_vehicle_map_future_data(
            this.user.tenant, start, end,
            this.selected_vehicle || '', this.selected_driver || ''
          )
        } else {
          if (this.booking_search) {
            resp = await get_tbn_vehicle_map_future_charter_data(this.user.tenant, this.booking_search)
          } else {
            resp = await get_tbn_vehicle_map_future_data(
                this.user.tenant, start, end,
                this.selected_vehicle || '', this.selected_driver || '',
                this.selected_booking_category || ''
            )
          }
        }
        const data = resp && resp.value ? (resp.value.data || {}) : {}
        this.summary = data.summary || {}
        if (this.summary && this.summary.timezone && moment.tz) moment.tz.setDefault(this.summary.timezone)
        this.items = data.results || []
        this.items_full = data.results || []
        this.vehicle_type_options = (this.summary && this.summary.vehicle_types) ? this.summary.vehicle_types : []
        await this.updateMap()
      } catch (err) {
        console.error('🚨 load_data error:', err)
        this.$emit('showAlert', 'Error retrieving data: ' + (err && (err.message || err)), 'danger', 7000)
      } finally {
        this.loading = false
      }
    },

    resetForTenantChange() {
        this.items = []
        this.items_full = []
        this.summary = []
        this.vehicle_type_options = []
        this.selected_vehicle_type = []

        this.sliderTime = null
        this.autoSimulatePositions = false
        this.vehicleGroups = {}
        this.vehicleIds = []
        this.polylines = []

        this.clearMarkers()
      },

    clearMarkers() {
      this.staticMarkers.forEach(m => m.setMap(null))
      this.staticMarkers = []
      this.vehicleMarkers.forEach(m => m.setMap(null))
      this.vehicleMarkers = []
      this.polylines = []
      this.segments = []
    },

    async updateMap() {
      if (!this.google) return;
      this.clearMarkers();
      this.segments = [];
      this.polylines = [];

      var geocoder = new this.google.maps.Geocoder();
      var bounds = new this.google.maps.LatLngBounds();
      var that = this;

      function resolveLocation(trip, addr) {
        var latKey = addr === 'pickup' ? 'pickup_lat' : 'dropoff_lat'
        var lngKey = addr === 'pickup' ? 'pickup_lng' : 'dropoff_lng'
        if (trip[latKey] != null && trip[lngKey] != null &&
            isLatLngInUsCa(trip[latKey], trip[lngKey])) {
          return Promise.resolve(new that.google.maps.LatLng(Number(trip[latKey]), Number(trip[lngKey])))
        }
        return new Promise(function(res, rej) {
          geocoder.geocode({ address: trip[addr] }, function(r, s) {
            if (s === 'OK' && r && r[0] && r[0].geometry && r[0].geometry.location) {
              var loc = r[0].geometry.location
              if (isLatLngInUsCa(loc.lat(), loc.lng())) res(loc)
              else rej('OUT_OF_BOUNDS')
            } else {
              rej(s)
            }
          })
        })
      }

      // build segment jobs prefer coordinates when present and within US/CA
      var jobs = this.items.map(function(t) {
        return Promise.all(['pickup','dropoff'].map(function(addr) {
          return resolveLocation(t, addr)
        })).then(function(results) {
          var p = results[0];
          var d = results[1];
          var vid = (typeof t.vehicle === 'string' && t.vehicle && t.vehicle.trim()) ? t.vehicle :
                    (t.vehicle && typeof t.vehicle.id === 'string') ? t.vehicle.id : t.bookingID;
          var start = moment(t.pickup_time, 'MM/DD hh:mm A').valueOf();
          var end   = moment(t.dropoff_time,'MM/DD hh:mm A').valueOf();
          if (p) bounds.extend(p);
          if (d) bounds.extend(d);
          return {
            vehicleId: vid,
            pickupPos: { lat: p.lat(), lng: p.lng() },
            dropoffPos: { lat: d.lat(), lng: d.lng() },
            startTime: start,
            endTime: end,
            trip: t
          };
        }).catch(function(err) {
          // if both address geocoding and coords fail skip this trip quietly
          return null;
        });
      });

      var allSegs = await Promise.all(jobs);
      this.segments = allSegs.filter(function(s) { return !!s; });
      if (!this.segments.length) return
      this.segments.sort(function(a, b) { return a.startTime - b.startTime; });

      // group by vehicle
      var groups = {};
      this.segments.forEach(function(seg) {
        if (!groups[seg.vehicleId]) groups[seg.vehicleId] = [];
        groups[seg.vehicleId].push(seg);
      });
      Object.keys(groups).forEach(function(k) {
        groups[k].sort(function(a, b){ return a.startTime - b.startTime; });
      });
      this.vehicleGroups = groups;

      // polylines
      this.polylines = this.segments.map(function(seg, i) {
          return { id: 'poly-' + i, path: [seg.pickupPos, seg.dropoffPos], color: '#0000FF' };
      });

      // static markers for pickups/dropoffs
      var mapObj = this.$refs.mapRef.$mapObject;
      var self = this;
      this.segments.forEach(function(seg) {
        var pickupMarker = new self.google.maps.Marker({
          map: mapObj,
          position: seg.pickupPos,
          icon: { path: self.google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: 'green', fillOpacity: 1, strokeWeight: 1 },
          zIndex: 1
        });
        pickupMarker.addListener('click', function() { self.openInfoWindow({ trip: seg.trip, position: seg.pickupPos }); });
        self.staticMarkers.push(pickupMarker);

        var dropMarker = new self.google.maps.Marker({
          map: mapObj,
          position: seg.dropoffPos,
          icon: { path: 'M-8,-8 L-8,8 L8,8 L8,-8 Z', fillColor: 'red', fillOpacity: 1, strokeWeight: 1 },
          zIndex: 1
        });
        dropMarker.addListener('click', function() { self.openInfoWindow({ trip: seg.trip, position: seg.dropoffPos }); });
        self.staticMarkers.push(dropMarker);
      });

      // vehicle markers at current slider time
      this.vehicleIds = Object.keys(this.vehicleGroups);
      var currentTime = this.sliderTime != null ? this.sliderTime : this.sliderMin;
      var self2 = this;
      this.vehicleMarkers = this.vehicleIds.map(function(id) {
        var segs = self2.vehicleGroups[id];
        var seg  = self2._chooseSegment(segs, currentTime);
        var pos  = self2.posForTime(seg, currentTime);
        var color= (currentTime >= seg.startTime && currentTime <= seg.endTime) ? 'green' : 'grey';
        return new self2.google.maps.Marker({
          map: self2.$refs.mapRef.$mapObject,
          position: pos,
          icon: self2._buildIcon(seg, currentTime, color),
          title: String(seg.trip.bookingID),
          zIndex: 1000
        });
      });

      if (this.$refs.mapRef) this.$refs.mapRef.$mapObject.fitBounds(bounds);
      if (this.sliderTime == null) this.sliderTime = this.sliderMin;
      this.simulatePositions();
    },
    posForTime(seg, time) {
      if (time <= seg.startTime) return seg.pickupPos
      if (time >= seg.endTime)   return seg.dropoffPos
      const f = (time - seg.startTime) / (seg.endTime - seg.startTime)
      return { lat: seg.pickupPos.lat + (seg.dropoffPos.lat - seg.pickupPos.lat) * f,
               lng: seg.pickupPos.lng + (seg.dropoffPos.lng - seg.pickupPos.lng) * f }
    },

    async runSimulatePositions() {
      while(this.autoSimulatePositions) {
        if (this.sliderTime < this.sliderMax)
          this.sliderTime += this.sliderStep
        else
          this.sliderTime = this.sliderMin

        this.simulatePositions()
        await this.sleep(100)
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    simulatePositions() {
      this.vehicleMarkers.forEach((marker, idx) => {
        const seg = this._chooseSegment(this.vehicleGroups[this.vehicleIds[idx]], this.sliderTime)
        const pos = this.posForTime(seg, this.sliderTime)
        const color = (this.sliderTime >= seg.startTime && this.sliderTime <= seg.endTime) ? 'green' : 'grey'
        marker.setPosition(pos)
        marker.setIcon(this._buildIcon(seg, this.sliderTime, color))
      })
    },

    _chooseSegment(segs, time) {
      const active   = segs.find(s => time >= s.startTime && time <= s.endTime)
      if (active) return active
      const upcoming = segs.filter(s => s.startTime > time).sort((a, b) => a.startTime - b.startTime)
      if (upcoming.length) return upcoming[0]
      const done     = segs.filter(s => s.endTime < time).sort((a, b) => b.endTime - a.endTime)
      return done[0] || segs[0]
    },

    _buildIcon(seg, time, overrideColor) {
      const color = overrideColor || (time >= seg.startTime && time <= seg.endTime ? 'green' : 'grey')
      let heading = 0
      if (this.google.maps.geometry && this.google.maps.geometry.spherical &&
          typeof this.google.maps.geometry.spherical.computeHeading === 'function') {
        heading = this.google.maps.geometry.spherical.computeHeading(
          new this.google.maps.LatLng(seg.pickupPos), new this.google.maps.LatLng(seg.dropoffPos)
        )
      }
      return { path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3,
               rotation: heading, fillColor: color, fillOpacity: 1, strokeWeight: 1 }
    },

    openInfoWindow({ trip, position }) {
      this.infoWindow.position = position
      this.infoWindow.content = `
        <div>
          <b>Booking:</b> ${trip.bookingID}<br/>
          <b>Org:</b> ${trip.organizationName}<br/>
          <b>Route:</b> ${trip.route_name}<br/>
          <b>Vehicle:</b> ${trip.vehicle || trip.vehicle_type}<br/>
          <b>Driver:</b> ${trip.driver}<br/>
          <b>Pickup:</b> ${trip.pickup_time} @ ${trip.pickup}<br/>
          <b>Dropoff:</b> ${trip.dropoff_time} @ ${trip.dropoff}
        </div>`
      this.infoWindow.open = true
    },

    shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate,'MM-DD-YYYY').add(shift,'days').format('MM-DD-YYYY')
      this.details_toDate   = moment(this.details_toDate,  'MM-DD-YYYY').add(shift,'days').format('MM-DD-YYYY')
      this.autoSimulatePositions = false
      this.sliderTime = this.sliderMin
    },
    filter_items() {
      this.items = this.items_full;

      if (this.booking_search !== '') {
        var search = this.booking_search;
        var found_items = this.items.filter(function(item) {
          return item.booking_hash === search;
        });
        if (found_items.length > 0) {
          this.items = found_items;
        }
      }

      var sel = this.selected_vehicle_type;
      if (sel && sel.length > 0) {
        this.items = this.items.filter(function(item) {
          return sel.indexOf(item.vehicle_type) !== -1;
        });
      }

      this.updateMap();
    },
  },
}
</script>

<style scoped>
.marker-label {
  opacity: 0.8;
  bottom: 40px;
  position: relative;
  border: 1px solid black;
  background-color: #999;
  color: white !important;
  border-radius: 4px;
  padding: 2px;
}

/* filter bar geometry (mobile-friendly wrap) */
.map-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 18px;
  font-size: 18px;
  font-weight: bold;
}
.mc-group {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}
.mc-datepicker { font-size: 18px; font-weight: bold; width: 140px; }
.mc-select { font-size: 18px; font-weight: bold; }
.mc-select--driver { width: 220px; }
.mc-select--vehicle { width: 220px; }
.mc-select--category { width: 200px; }
.mc-search { width: 165px; }
.mc-typerow { margin: 20px; }
/* the slider row's 100px side margins were dead width on a phone */
.mc-sliderrow { margin: 20px 100px; font-size: 18px; }
@media (max-width: 640px) {
  .map-controls { justify-content: flex-start; font-size: 15px; gap: 6px 14px; }
  /* 16px floor keeps ios safari from zooming the viewport on focus */
  .mc-datepicker, .mc-select, .mc-search { font-size: 16px; }
  .mc-select { max-width: 100%; }
  .mc-typerow { margin: 14px 0; }
  .mc-sliderrow { margin: 14px 0; font-size: 15px; }
}
</style>
